/**
 * Number Set Service - เลขชุดของลูกค้า (เดียวกับ user-classic)
 */
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'

export interface NumberSetItem {
  prizeTypeCode: string
  typeLabel: string
  numbers: string
}

export interface CustomerNumberSet {
  _id: string
  tenantId: string
  userId: string
  name: string
  items: NumberSetItem[]
  createdAt: string
  updatedAt: string
}

function getBaseUrl(): string {
  return (import.meta.env as { VITE_API_BASE_URL?: string }).VITE_API_BASE_URL || 'http://localhost:8081'
}

function getTenantId(): string {
  const resolved = getRuntimeTenantIdSync()
  if (resolved) return resolved
  throw new Error('Tenant ID is required but not found.')
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken')
  const tenantId = getTenantId()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId,
  }
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

const base = () => `${getBaseUrl()}/api/v1/lottery/tenant/${getTenantId()}/customer-number-sets`

export const numberSetService = {
  async list(): Promise<CustomerNumberSet[]> {
    const res = await fetch(base(), { method: 'GET', headers: getAuthHeaders(), credentials: 'include' })
    if (res.status === 404) return []
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      const msg = err.error || 'Failed to list number sets'
      if (msg === 'Not Found' || String(msg).toLowerCase().includes('not found')) {
        throw new Error('ยังไม่มีเลขชุด หรือไม่สามารถโหลดรายการได้ในขณะนี้')
      }
      throw new Error(msg)
    }
    const data = await res.json()
    return Array.isArray(data.data) ? data.data : []
  },

  async get(id: string): Promise<CustomerNumberSet | null> {
    const res = await fetch(`${base()}/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include',
    })
    if (res.status === 404) return null
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error || 'Failed to get number set')
    }
    const data = await res.json()
    return data.data || null
  },

  async create(payload: { name: string; items: NumberSetItem[] }): Promise<CustomerNumberSet> {
    const res = await fetch(base(), {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText, message: '' }))
      const msg = err.error || err.message || res.statusText
      if (res.status === 404 || String(msg).toLowerCase().includes('not found')) {
        throw new Error('ไม่พบ API บันทึกเลขชุด กรุณาลองใหม่หรือติดต่อผู้ดูแลระบบ')
      }
      throw new Error(typeof msg === 'string' ? msg : 'บันทึกเลขชุดไม่สำเร็จ')
    }
    const data = await res.json()
    return data.data
  },

  async update(id: string, payload: { name?: string; items?: NumberSetItem[] }): Promise<CustomerNumberSet> {
    const res = await fetch(`${base()}/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error || 'Failed to update number set')
    }
    const data = await res.json()
    return data.data
  },

  async delete(id: string): Promise<void> {
    const res = await fetch(`${base()}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    })
    if (res.status !== 200 && res.status !== 204) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error || 'Failed to delete number set')
    }
  },
}
